/**
 * App.tsx — Archivo principal de la aplicación
 *
 * Este archivo define todas las RUTAS de la app.
 * Una "ruta" es como una dirección URL: cuando el usuario va a "/menu",
 * React muestra el componente Menu. Cuando va a "/", muestra Login, etc.
 *
 * Piensa en esto como el mapa de navegación de toda la aplicación.
 */

import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import { App as CapacitorApp } from '@capacitor/app';

// Importamos cada pantalla (página) de la aplicación
import Login from './pages/Login';                       // Pantalla de inicio de sesión
import Menu from './pages/Menu';                         // Menú principal con botones
import ConsultaCliente from './pages/1_0_consultacliente'; // Ver datos del cliente
import FacturasCliente from './pages/FacturasClienteNuevo'; // Ver facturas del cliente
import Pasarela from './pages/Pasarela';                 // Pasarela de pago (seleccionar método)
import Promesa from './pages/Promesa';                   // Promesa de pago
import ConfirmacionPago from './pages/ConfirmacionPago'; // Pantalla de confirmación tras pagar
import Pagos from './pages/Pagos';                       // Pantalla de pagos (acceso por link)
import Tickets from './pages/Tickets';                   // Tickets de soporte técnico
import Clientes from './pages/Clientes';                 // Lista de todos los clientes (solo admin)
import AbonoFactura from './pages/AbonoFactura';          // Abono a factura con comprobante (validado por IA)
import CambiarClaveWifi from './pages/CambiarClaveWifi';  // Cambiar contraseña WiFi del router (GenieACS)
import PagoClientes from './pages/PagoClientes';          // Consultar cédula para registrar pago (admin)
import DispositivosConectados from './pages/DispositivosConectados'; // Lista de dispositivos conectados al router (GenieACS)
import Apariencia from './pages/Apariencia';              // Elegir tema claro / oscuro / automático
import PromocionesInternet from './pages/PromocionesInternet'; // Planes de internet + pedir por WhatsApp
import SubirComprobante from './pages/SubirComprobante';       // Leer un comprobante bancario con Vision + IA (admin)
import PageTransition from './components/PageTransition'; // Logo parpadeante entre pantallas
import TypebotBubble from './components/TypebotBubble';   // Burbuja de chat de soporte (solo administradores)
import CierreInactividad from './components/CierreInactividad'; // Cierra la sesión tras 5 min sin actividad
import { useTema } from './hooks/useTema';                // Aplica el tema elegido (claro/oscuro/automático)

// Escucha deep links sisnetel:// dentro del Router (tiene acceso a useNavigate)
const DeepLinkHandler: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const listener = CapacitorApp.addListener('appUrlOpen', ({ url }) => {
      // url = "sisnetel://confirmacion?id=32805807&clientTransactionId=XYZ"
      if (!url.startsWith('sisnetel://')) return;
      const withHttps = url.replace('sisnetel://', 'https://sisnetel.app/');
      const parsed    = new URL(withHttps);
      const id        = parsed.searchParams.get('id');
      const clientTxId = parsed.searchParams.get('clientTransactionId');
      if (id && clientTxId) {
        const stored = sessionStorage.getItem('payphone_cliente');
        const c = stored ? JSON.parse(stored) : {};
        navigate(
          `/confirmacionpago?id=${id}&clientTransactionId=${clientTxId}` +
          `&cedula=${encodeURIComponent(c.cedula ?? '')}` +
          `&monto=${c.monto ?? ''}` +
          `&nombre=${encodeURIComponent(c.nombre ?? '')}`
        );
      }
    });
    return () => { listener.then(l => l.remove()); };
  }, [navigate]);

  return null;
};

const App: React.FC = () => {
  // Aplica el tema elegido (claro/oscuro/automático) en toda la app y, si está
  // en automático, reacciona cuando el teléfono cambia de modo.
  useTema();

  return (
    <Router>
      <DeepLinkHandler />
      <CierreInactividad />
      <PageTransition />
      <TypebotBubble />
      <Routes>
        {/* path="/" → página de inicio, siempre muestra el Login */}
        <Route path="/" element={<Login />} />

        {/* path="/menu" → menú principal del usuario */}
        <Route path="/menu" element={<Menu />} />

        {/* path="/clientes" → datos personales del cliente logueado */}
        <Route path="/clientes" element={<ConsultaCliente />} />

        {/* path="/clientes/facturas" → facturas del cliente logueado */}
        <Route path="/clientes/facturas" element={<FacturasCliente />} />

        {/* path="/pasarela/:facturaId" → el ":facturaId" es un parámetro dinámico
            Ejemplo: /pasarela/1234 carga la pasarela para la factura 1234 */}
        <Route path="/pasarela/:facturaId" element={<Pasarela />} />

        {/* path="/confirmacionpago" → se muestra después de completar un pago */}
        <Route path="/confirmacionpago" element={<ConfirmacionPago />} />

        {/* path="/menu/promesa" → formulario para hacer una promesa de pago */}
        <Route path="/menu/promesa" element={<Promesa />} />

        {/* path="/r/:cedula64" → acceso directo a pagos por link (cédula en base64)
            Ejemplo: /r/MTIzNDU2Nzg5 donde MTIzNDU2Nzg5 es la cédula codificada */}
        <Route path="/r/:cedula64" element={<Pagos />} />

        {/* path="/menu/tickets" → lista de tickets de soporte del cliente */}
        <Route path="/menu/tickets" element={<Tickets />} />

        {/* path="/menu/clientes" → lista de todos los clientes (solo visible para administradores) */}
        <Route path="/menu/clientes" element={<Clientes />} />

        {/* path="/menu/abono" → abono a factura con comprobante (validado por IA) */}
        <Route path="/menu/abono" element={<AbonoFactura />} />

        {/* path="/menu/wifi" → cambiar contraseña WiFi del router (GenieACS) */}
        <Route path="/menu/wifi" element={<CambiarClaveWifi />} />

        {/* path="/menu/pagoclientes" → admin ingresa cédula para consultar/pagar cuenta de un cliente */}
        <Route path="/menu/pagoclientes" element={<PagoClientes />} />

        {/* path="/menu/dispositivos" → lista de dispositivos conectados al router (GenieACS) */}
        <Route path="/menu/dispositivos" element={<DispositivosConectados />} />

        {/* path="/menu/apariencia" → elegir tema claro / oscuro / automático */}
        <Route path="/menu/apariencia" element={<Apariencia />} />

        {/* path="/menu/promociones-internet" → planes de internet con carrusel y pedido por WhatsApp */}
        <Route path="/menu/promociones-internet" element={<PromocionesInternet />} />

        {/* path="/menu/comprobantes" → subir un comprobante bancario y leer sus datos (solo administradores) */}
        <Route path="/menu/comprobantes" element={<SubirComprobante />} />
      </Routes>
    </Router>
  );
};

export default App;
